import React, { useState } from "react";
import Heading from "@/components/elements/Heading";
import Label from "@/components/elements/Label";
import TextInput from "@/components/elements/TextInput";
import TextArea from "@/components/elements/TextArea";
import Dropdown from "@/components/elements/Dropdown";
import { usePage } from "@inertiajs/inertia-react";
import DatePicker from "react-datepicker";

/**
 * OrderForm component that handles customer information, delivery details, and order submission
 * Features admin-specific controls and comprehensive order validation
 *
 * @param {Object} props - Component props
 * @param {Array} props.products - Array of products in the shopping cart
 * @param {Object} props.contact - Customer contact and delivery information
 * @param {Function} props.updateContact - Function to update contact information
 * @param {Object} props.session - Current session data
 * @param {Function} props.updateSession - Function to update session data
 * @param {Array} props.customers - Available customers (admin only)
 * @param {Array} props.contacts - Available contacts for selected customer
 * @param {Function} props.updateDropdown - Function to handle dropdown changes
 * @param {Function} props.onOrder - Function to handle order submission
 * @param {Object} props.errors - Form validation errors
 */
const OrderForm = ({
    products,
    contact,
    updateContact,
    session,
    updateSession,
    customers,
    contacts,
    updateDropdown,
    onOrder,
    errors
}) => {
    // Get authenticated user data
    const { auth } = usePage().props;

    // State for date picker debouncing
    const [dateTimeout, setDateTimeout] = useState(0);

    // Static configuration data
    const deliveryOptions = [
        { value: 1, name: "Bezorgen" },
        { value: 2, name: "Afhalen/lokaal leveren" },
    ];

    /**
     * Handle delivery date changes with debouncing
     * Updates immediately for UI responsiveness, then waits 300ms before triggering API call
     *
     * @param {Date} date - The selected date
     */
    function handleDateChange(date) {
        // Clone contact and update the delivery_date_preference
        let updatedContact = { ...contact };
        updatedContact.delivery_date_preference = date ? toCustomDateFormat(date) : null;

        // Update immediately without triggering API call (false parameter)
        updateContact(updatedContact, false);

        // Clear existing timeout to restart debounce timer
        if (dateTimeout) {
            clearTimeout(dateTimeout);
        }

        // Set new timeout for delayed update with API call
        setDateTimeout(
            setTimeout(() => {
                updateContact(updatedContact); // Trigger API call after delay
            }, 300),
        );
    }

	function toCustomDateFormat(date) {
		// Format as dmY (day-month-year) to prevent timezone offset issues
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = date.getFullYear();
		return `${day}-${month}-${year}`;
	}

	function parseCustomDateFormat(dateString) {
		// Parse dmY format (dd-MM-yyyy) back to Date object
		if (!dateString) return null;
		
		// Handle dd-MM-yyyy format
		const parts = dateString.split('-');
		if (parts.length === 3) {
			const day = parseInt(parts[0], 10);
			const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in JS
			const year = parseInt(parts[2], 10);
			return new Date(year, month, day);
		}
		
		// Fallback to default Date parsing
		return new Date(dateString);
	}

    return (
        <div className={`${products.length > 0 ? "col-lg-6" : "d-none"}`}>
            <div className="background-content-gray shoppingpadding shoppingpaddingright h-100">
                <Heading heading="Order"></Heading>

                {/* Customer selection (admin only) */}
                <div className="details mb-4">
                    <div className="details">
                        {auth.user.admin && (
                            <>
                                <div className="question mb-3">
                                    <Label label="Klant" small />
                                    <Dropdown
                                        properties={contact}
                                        property={"company_id"}
                                        updateProperties={updateDropdown}
                                        options={customers}
                                    />
                                </div>
                                <div className="question mb-3">
                                    <Label label="Contact" small />
                                    <Dropdown
                                        properties={contact}
                                        property={"contact_id"}
                                        clear
                                        updateProperties={updateDropdown}
                                        options={contacts}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {/* Order reference */}
                    <div className="question mb-3">
                        <Label label="Referentie" small />
                        <TextInput
                            properties={session}
                            property="description"
                            maxLength={30}
                            updateProperties={updateSession}
                            error={errors.hasOwnProperty("description")}
                        />
                    </div>
                </div>

                {/* Delivery information */}
                <Heading heading="Aflevergegevens"></Heading>
                <div className="details">
                    {/* Delivery method */}
                    <div className="question mb-3">
                        <Label label="Levermethode" small />
                        <Dropdown
                            properties={contact}
                            property={"delivery"}
                            updateProperties={updateDropdown}
                            options={deliveryOptions}
                        />
                    </div>

                    {/* Delivery address (only for delivery option) */}
                    {contact.delivery === 1 && (
                        <>
                            <div className="question mb-3">
                                <Label
                                    label="Afleveradresnaam"
                                    small
                                />
                                <TextInput
                                    properties={contact}
                                    property="delivery_company_name"
                                    updateProperties={updateContact}
                                    error={errors.hasOwnProperty("delivery_company_name")}
                                />
                            </div>

                            <div className="question mb-3">
                                <Label label="Adres" small />
                                <TextInput
                                    properties={contact}
                                    property="address"
                                    maxLength={30}
                                    updateProperties={updateContact}
                                    error={errors.hasOwnProperty("address")}
                                />
                            </div>

                            {/* Zip code and city */}
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="question mb-3">
                                        <Label
                                            label="Postcode"
                                            small
                                        />
                                        <TextInput
                                            properties={contact}
                                            property="zipcode"
                                            maxLength={10}
                                            updateProperties={updateContact}
                                            error={errors.hasOwnProperty("zipcode")}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="question mb-3">
                                        <Label
                                            label="Plaats"
                                            small
                                        />
                                        <TextInput
                                            properties={contact}
                                            property="city"
                                            maxLength={30}
                                            updateProperties={updateContact}
                                            error={errors.hasOwnProperty("city")}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

	                <div className="question mb-3">
		                <Label label="Gewenste afleverdatum" small />
		                <div>
			                <DatePicker
				                selected={contact.delivery_date_preference ? parseCustomDateFormat(contact.delivery_date_preference) : null}
				                onChange={handleDateChange}
				                className={`form-control ${errors.hasOwnProperty("delivery_date_preference") ? "is-invalid" : ""}`}
				                dateFormat="dd-MM-yyyy"
				                placeholderText="Selecteer datum"
				                name="delivery_date_preference"
			                />
		                </div>
	                </div>

                    {/* Contact information */}
                    <div className="question mb-3">
                        <Label label="Telefoon" small />
                        <TextInput
                            properties={contact}
                            property="telephone"
                            maxLength={20}
                            updateProperties={updateContact}
                        />
                    </div>

                    <div className="question mb-3">
                        <Label label="Toelichting" small />
                        <TextArea
                            properties={contact}
                            property="toelichting"
                            maxLength={60}
                            updateProperties={updateContact}
                        />
                    </div>

                    {/* Order button */}
                    <a
                        name={"Bestellen"}
                        href="#"
                        className="btn-orange m-0"
                        onClick={onOrder}
                    >
                        Bestellen
                    </a>
                </div>
            </div>
        </div>
    );
};

export default OrderForm;
